import { toast } from "@/hooks/use-toast";
import { createUserMediaMonitor, UserMediaMonitor } from "./lib/track-monitor";
import { emitMediaEvent, userMediaStore } from "./media-monitor";
import { agentStore } from "../voice-agent-store";
import type { DeviceKind } from "./types";

let controllerMonitor: UserMediaMonitor | null = null;
let lastAppliedAudioTrackId: string | null = null;

const applyCustomAudioTrackToAgent = async () => {
  const agentState = agentStore.getState();
  const agent = agentState.instance;
  if (!agent) return;
  if (agentState.state !== "call-started") {
    return;
  }

  const monitor = userMediaStore.getState().monitor;
  const customAudioTrack = monitor?.customAudioTrack ?? null;

  if (!customAudioTrack) {
    lastAppliedAudioTrackId = null;
    return;
  }

  if (customAudioTrack.id === lastAppliedAudioTrackId) {
    return;
  }

  try {
    await agent.setCustomInputSettings({
      audio: {
        settings: {
          customTrack: customAudioTrack,
        },
      },
    });

    lastAppliedAudioTrackId = customAudioTrack.id;
  } catch {
    lastAppliedAudioTrackId = null;
  }
};

const updateDevicesFromMonitor = (newValue: {
  media: {
    microphone: any;
    video: any;
    speaker: any;
  };
  stream?: MediaStream | null;
}) => {
  const state = userMediaStore.getState();
  userMediaStore.setState({
    ...state,
    userMedia: {
      ...state.userMedia,
      data: newValue.stream ?? undefined,
    },
    devices: {
      audio: {
        health: newValue.media.microphone.health,
        activeDeviceId: newValue.media.microphone.activeDeviceId,
        availableDevices: newValue.media.microphone.availableDevices,
        stream: newValue.media.microphone.stream,
        dataStatus: newValue.media.microphone.dataStatus,
        fetchStatus: newValue.media.microphone.fetchStatus,
      },
      video: {
        health: newValue.media.video.health,
        activeDeviceId: newValue.media.video.activeDeviceId,
        availableDevices: newValue.media.video.availableDevices,
        stream: newValue.media.video.stream,
        dataStatus: newValue.media.video.dataStatus,
        fetchStatus: newValue.media.video.fetchStatus,
      },
      speaker: {
        health: newValue.media.speaker.health,
        activeDeviceId: newValue.media.speaker.activeDeviceId,
        availableDevices: newValue.media.speaker.availableDevices,
        stream: newValue.media.speaker.stream,
        dataStatus: newValue.media.speaker.dataStatus,
        fetchStatus: newValue.media.speaker.fetchStatus,
      },
    },
  });

  applyCustomAudioTrackToAgent();
};

const ensureMonitor = () => {
  if (controllerMonitor) return controllerMonitor;

  const monitor = createUserMediaMonitor({
    onMediaChange: (_oldValue, newValue) => {
      updateDevicesFromMonitor(newValue);
    },
    onDeviceChange: (kind, device) => {
      if (kind !== "audio") return;

      if (device) {
        const previousDeviceId =
          userMediaStore.getState().devices.audio.activeDeviceId ?? null;
        if (device.deviceId && device.deviceId === previousDeviceId) {
          return;
        }

        emitMediaEvent("device-changed", {
          kind: "audio",
          deviceId: device.deviceId,
          deviceLabel: device.label ?? null,
          previousDeviceId,
        });
      } else {
        emitMediaEvent("device-unreliable", {
          kind: "audio",
          reason: "Microphone disconnected",
        });
      }
    },
    onDeviceError: (kind, error) => {
      if (kind !== "audio") return;
      emitMediaEvent("device-unreliable", {
        kind: "audio",
        reason: error,
      });
      toast({
        title: "Microphone error",
        description: error,
        variant: "destructive",
      });
    },
  });

  controllerMonitor = monitor;
  userMediaStore.setState({
    ...userMediaStore.getState(),
    monitor: monitor,
  });
  return monitor;
};

export const initializeMedia = async (
  type: "audio" | "video" | "audio-video" = "audio-video",
) => {
  const set = userMediaStore.setState;
  const get = userMediaStore.getState;
  const state = get();
  const isInitialized =
    (type === "audio" && state.initializationState.audio === "initialized") ||
    (type === "video" && state.initializationState.video === "initialized") ||
    (type === "audio-video" &&
      state.initializationState.audio === "initialized" &&
      state.initializationState.video === "initialized");

  if (isInitialized) return;

  // Set loading state before requesting permissions
  set((state) => ({
    ...state,
    initializationState: {
      ...state.initializationState,
      ...(type === "audio" && { audio: "loading" }),
      ...(type === "video" && { video: "loading" }),
      ...(type === "audio-video" && { audio: "loading", video: "loading" }),
    },
  }));

  set((state) => ({
    ...state,
    userMedia: {
      ...state.userMedia,
      fetchStatus: "fetching",
      dataStatus:
        state.userMedia.data === undefined
          ? "loading"
          : state.userMedia.dataStatus,
    },
  }));

  const monitorInstance = ensureMonitor();

  try {
    const stream = await monitorInstance.request(type);

    if (!stream) {
      throw new Error("Failed to get media stream");
    }

    set((state) => ({
      ...state,
      userMedia: {
        fetchStatus: "success",
        dataStatus: "success",
        data: stream,
      },
    }));

    const audioTracks = stream.getAudioTracks();
    const videoTracks = stream.getVideoTracks();

    set((state) => ({
      ...state,
      initializationState: {
        ...state.initializationState,
        ...(audioTracks.length > 0 && { audio: "initialized" }),
        ...(videoTracks.length > 0 && { video: "initialized" }),
      },
    }));
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error";
    const kinds = (
      type === "audio-video" ? ["audio", "video"] : [type]
    ) as DeviceKind[];

    set((state) => ({
      ...state,
      devices: {
        ...state.devices,
        audio: kinds.includes("audio")
          ? {
              ...state.devices.audio,
              health: {
                status: "error",
                error: errorMessage,
                lastActive: Date.now(),
              },
            }
          : state.devices.audio,
        video: kinds.includes("video")
          ? {
              ...state.devices.video,
              health: {
                status: "error",
                error: errorMessage,
                lastActive: Date.now(),
              },
            }
          : state.devices.video,
      },
      initializationState: {
        ...state.initializationState,
        ...(kinds.includes("audio") && { audio: "error" }),
        ...(kinds.includes("video") && { video: "error" }),
      },
    }));

    toast({
      title: "Unable to detect device",
      description: "Please enable camera and microphone permissions.",
      variant: "destructive",
    });

    set((state) => ({
      ...state,
      userMedia: {
        ...state.userMedia,
        fetchStatus: "error",
        dataStatus: state.userMedia.data === undefined ? "error" : "success",
      },
    }));
  }
};

export const fetchDevices = async () => {
  const monitor = ensureMonitor();
  const set = userMediaStore.setState;

  set((state) => ({
    ...state,
    devices: {
      ...state.devices,
      audio: {
        ...state.devices.audio,
        fetchStatus: "fetching",
        dataStatus:
          state.devices.audio.availableDevices &&
          state.devices.audio.availableDevices.length === 0
            ? "loading"
            : state.devices.audio.dataStatus,
      },
      video: {
        ...state.devices.video,
        fetchStatus: "fetching",
        dataStatus:
          state.devices.video.availableDevices &&
          state.devices.video.availableDevices.length === 0
            ? "loading"
            : state.devices.video.dataStatus,
      },
      speaker: {
        ...state.devices.speaker,
        fetchStatus: "fetching",
        dataStatus:
          state.devices.speaker.availableDevices &&
          state.devices.speaker.availableDevices.length === 0
            ? "loading"
            : state.devices.speaker.dataStatus,
      },
    },
  }));

  try {
    await monitor.onDeviceChange();
  } catch {
    set((state) => ({
      ...state,
      devices: {
        ...state.devices,
        audio: {
          ...state.devices.audio,
          fetchStatus: "error",
          dataStatus:
            state.devices.audio.availableDevices &&
            state.devices.audio.availableDevices.length === 0
              ? "error"
              : "success",
        },
        video: {
          ...state.devices.video,
          fetchStatus: "error",
          dataStatus:
            state.devices.video.availableDevices &&
            state.devices.video.availableDevices.length === 0
              ? "error"
              : "success",
        },
        speaker: {
          ...state.devices.speaker,
          fetchStatus: "error",
          dataStatus:
            state.devices.speaker.availableDevices &&
            state.devices.speaker.availableDevices.length === 0
              ? "error"
              : "success",
        },
      },
    }));
  }
};

export const selectAudioInput = async (deviceId: string) => {
  const monitor = ensureMonitor();
  const state = userMediaStore.getState();
  const shouldResolveDefault =
    deviceId === "default" || deviceId === "communications";
  const resolvedDevice = shouldResolveDefault
    ? state.devices.audio.availableDevices?.find(
        (d) =>
          d.deviceId !== "default" &&
          d.deviceId !== "communications" &&
          d.deviceId !== "",
      )
    : state.devices.audio.availableDevices?.find(
        (d) => d.deviceId === deviceId,
      );
  const resolvedDeviceId = resolvedDevice?.deviceId || deviceId;
  const previousDeviceId = state.devices.audio.activeDeviceId;

  if (resolvedDeviceId === previousDeviceId) {
    return;
  }

  await monitor.changeDeviceId("microphone", resolvedDeviceId);
};

export const selectVideoInput = async (deviceId: string) => {
  const monitor = ensureMonitor();
  const state = userMediaStore.getState();
  const shouldResolveDefault =
    deviceId === "default" || deviceId === "communications";
  const resolvedDevice = shouldResolveDefault
    ? state.devices.video.availableDevices?.find(
        (d) =>
          d.deviceId !== "default" &&
          d.deviceId !== "communications" &&
          d.deviceId !== "",
      )
    : state.devices.video.availableDevices?.find(
        (d) => d.deviceId === deviceId,
      );
  const resolvedDeviceId = resolvedDevice?.deviceId || deviceId;
  const previousDeviceId = state.devices.video.activeDeviceId;

  if (resolvedDeviceId === previousDeviceId) {
    return;
  }

  await monitor.changeDeviceId("video", resolvedDeviceId);
};

export const toggleAudio = () => {
  const monitor = ensureMonitor();
  monitor.stream?.getAudioTracks().forEach((t) => (t.enabled = !t.enabled));
};

export const toggleVideo = () => {
  const monitor = ensureMonitor();
  monitor.stream?.getVideoTracks().forEach((t) => (t.enabled = !t.enabled));
};

export const getActiveAudioDeviceId = () => {
  return userMediaStore.getState().devices.audio.activeDeviceId;
};

export const getMonitor = () => ensureMonitor();

if (typeof window !== "undefined") {
  ensureMonitor();
}
